import BN from 'bignumber.js';

import {
  STAKE_SETTINGS_SRFUEL,
  STAKE_DETAILS_SRFUEL,
  STAKE_SEND_SRFUEL,
  STAKE_SUCCESS_SRFUEL,
  STAKE_ERROR_SRFUEL,
  RESET_STAKE_SRFUEL,
  STAKE_BALANCE_SRFUEL,
  UNSTAKED_BALANCE_SRFUEL,
  UNSTAKE_TIME_SRFUEL,
  UNSTAKE_SETTINGS_SRFUEL,
  UNSTAKE_INFORMATION_SRFUEL,
  UNSTAKE_DETAILS_SRFUEL,
  UNSTAKE_IMMEDIATELY_DETAILS_SRFUEL,
  ESTIMATED_DAILY_REWARDS_SRFUEL,
  APR_SRFUEL,
  UNSTAKE_SEND_SRFUEL,
  UNSTAKE_SUCCESS_SRFUEL,
  WITHDRAW_DETAILS_SRFUEL,
  WITHDRAW_SEND_SRFUEL,
  WITHDRAW_SUCCESS_SRFUEL,
  HISTORY_SHOW_SRFUEL_STAKING,
  EARNED_REWARDS_BALANCE_SRFUEL,
  CLAIMING_FEE_PERCENT_SRFUEL,
} from './action-types';
import { getSRfuelBalance, approve } from './token-actions';
import sRfuelStakingContractService from '../services/contracts/srfuel-staking-contract-service';
import web3Service from '../services/web3-service';
import { SRFUEL } from '../constants/blockchain-constants';
import { getTotalStakedBalance } from './staking-actions';
import config from '../config/env';
import { calculateStakeBalance, getPrice } from '../helpers/stake-helpers';

const Event = {
  PRICE_UPDATED: '0x15819dd2fd9f6418b142e798d08a18d0bf06ea368f4480b7b0d3f75bd966bc48',
  TRANSFER: '0xddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef',
};

export const updateBalances = () => async (dispatch, getState) => {
  const { address } = getState().account;

  const [stakeBalance, price, unstakes] = await Promise.all([
    sRfuelStakingContractService.balanceOf(address),
    getPrice(sRfuelStakingContractService),
    sRfuelStakingContractService.getUnstake(address),
    dispatch(getSRfuelBalance()),
  ]);

  const { amount, applicableAt } = unstakes;

  // TODO: make one dispatch
  dispatch({
    type: STAKE_BALANCE_SRFUEL,
    payload: { stakeBalance: calculateStakeBalance(stakeBalance, price) },
  });
  dispatch({ type: UNSTAKE_TIME_SRFUEL, payload: { unstakedTime: applicableAt } });
  dispatch({ type: UNSTAKED_BALANCE_SRFUEL, payload: { unstakedBalance: amount } });
};

const estimatedBlocksIntervalStrategyRewards = (fromTimestamp, toTimestamp, strategy) => {
  const strategyFromTimestamp = Math.max(fromTimestamp, strategy[2]);
  const strategyToTimestamp = Math.max(toTimestamp, strategy[0]);

  if (strategyToTimestamp <= strategyFromTimestamp) {
    return '0';
  }

  return strategy[1] * (strategyToTimestamp - strategyFromTimestamp);
};

const estimatedBlocksIntervalRewards = async (
  fromTimestamp,
  toTimestamp,
  currentStrategy,
  nextStrategy,
) => {
  let result = estimatedBlocksIntervalStrategyRewards(fromTimestamp, toTimestamp, currentStrategy);
  result += estimatedBlocksIntervalStrategyRewards(fromTimestamp, toTimestamp, nextStrategy);
  return result;
};

const estimatedAPR = async () => {
  const [totalStaked, fromBlock, currentStrategy, nextStrategy] = await Promise.all([
    sRfuelStakingContractService.totalStaked(),
    web3Service.web3.eth.getBlockNumber(),
    sRfuelStakingContractService.currentStrategy(),
    sRfuelStakingContractService.nextStrategy(),
  ]);

  const { timestamp: fromTimestamp } = await web3Service.web3.eth.getBlock(fromBlock);

  const totalStakedNumber = new BN(totalStaked).toString(10);
  if (totalStakedNumber === '0') {
    return Infinity;
  }

  const toTimestamp = Math.max(currentStrategy.endsAt, nextStrategy.endsAt);
  if (toTimestamp <= fromTimestamp) {
    return '0';
  }

  const period = toTimestamp - fromTimestamp;
  const yearBlockPeriod = 3600 * 24 * 365;
  const yearBlocksCount = yearBlockPeriod / period;
  const estimatedBlocks = await estimatedBlocksIntervalRewards(
    fromTimestamp,
    toTimestamp,
    currentStrategy,
    nextStrategy,
  );
  const yearRewards = yearBlocksCount * estimatedBlocks;
  return (yearRewards / totalStaked) * 100;
};

const dailyReward = async (address) => {
  const [balanceOf, totalSupply, currentStrategy] = await Promise.all([
    sRfuelStakingContractService.balanceOf(address),
    sRfuelStakingContractService.totalSupply(),
    sRfuelStakingContractService.currentStrategy(),
  ]);

  if (totalSupply === '0') {
    return '0';
  }

  const dayBlockPeriod = 3600 * 24;
  return currentStrategy.perSecondReward * dayBlockPeriod * (balanceOf / totalSupply);
};

// STAKE

export const stakeSettings = () => async (dispatch) => {
  dispatch({ type: STAKE_SETTINGS_SRFUEL });
};

export const stakeDetails = (formValues) => async (dispatch) => {
  dispatch({ type: STAKE_DETAILS_SRFUEL, payload: formValues });
};

export const stakeSend = (formValues) => async (dispatch, getState) => {
  try {
    const { address } = getState().account;
    dispatch({ type: STAKE_SEND_SRFUEL });
    await approve(SRFUEL, address, config.REACT_APP_SRFUEL_STAKING_ADDRESS);
    const { amount } = formValues;
    const transaction = await sRfuelStakingContractService.stake(address, amount);
    await dispatch(updateBalances());

    // COMPLETE
    dispatch({ type: STAKE_SUCCESS_SRFUEL, payload: { id: transaction.transactionHash } });
  } catch (e) {
    dispatch({ type: STAKE_ERROR_SRFUEL, payload: { error: 'Stake failed' } });
  }
};

export const unstakeSettings = () => async (dispatch) => {
  dispatch({ type: UNSTAKE_SETTINGS_SRFUEL });
};

export const unstakeInformation = (formValues) => async (dispatch) => {
  dispatch({ type: UNSTAKE_INFORMATION_SRFUEL, payload: formValues });
};

export const unstakeDetails = () => async (dispatch) => {
  dispatch({ type: UNSTAKE_DETAILS_SRFUEL });
};

export const unstakeImmediatelyDetails = () => async (dispatch) => {
  dispatch({ type: UNSTAKE_IMMEDIATELY_DETAILS_SRFUEL });
};

export const unstakeSend = (formValues) => async (dispatch, getState) => {
  const { address } = getState().account;

  dispatch({ type: UNSTAKE_SEND_SRFUEL });

  const { amount } = formValues;

  try {
    const transaction = await sRfuelStakingContractService.unstake(address, amount);
    await dispatch(updateBalances());

    // COMPLETE
    dispatch({ type: UNSTAKE_SUCCESS_SRFUEL, payload: { id: transaction.transactionHash } });
  } catch (error) {
    dispatch({ type: STAKE_ERROR_SRFUEL, payload: { error: 'Unstake failed' } });
  }
};

export const unstakeImmediatelySend = (formValues) => async (dispatch, getState) => {
  const { address } = getState().account;

  dispatch({ type: UNSTAKE_SEND_SRFUEL });

  const { amount } = formValues;

  try {
    const transaction = await sRfuelStakingContractService.claim(address, amount);
    await dispatch(updateBalances());

    // COMPLETE
    dispatch({ type: UNSTAKE_SUCCESS_SRFUEL, payload: { id: transaction.transactionHash } });
  } catch (error) {
    dispatch({ type: STAKE_ERROR_SRFUEL, payload: { error: 'Unstake failed' } });
  }
};

export const withdrawUnstakedDetails = () => async (dispatch) => {
  dispatch({ type: WITHDRAW_DETAILS_SRFUEL });
};

export const withdrawUnstakedSend = () => async (dispatch, getState) => {
  const { address } = getState().account;

  dispatch({ type: WITHDRAW_SEND_SRFUEL });

  try {
    const transaction = await sRfuelStakingContractService.withdraw(address);
    await dispatch(updateBalances());

    // COMPLETE
    dispatch({ type: WITHDRAW_SUCCESS_SRFUEL, payload: { id: transaction.transactionHash } });
  } catch (error) {
    dispatch({ type: STAKE_ERROR_SRFUEL, payload: { error: 'Unstake failed' } });
  }
};

export const stakeReset = () => async (dispatch) => {
  dispatch({ type: RESET_STAKE_SRFUEL });
};

export const showHistory = () => async (dispatch) => {
  dispatch({ type: HISTORY_SHOW_SRFUEL_STAKING });
};

export const getStakedBalance = () => async (dispatch, getState) => {
  const { address } = getState().account;

  const [stakeBalance, price] = await Promise.all([
    sRfuelStakingContractService.balanceOf(address),
    getPrice(sRfuelStakingContractService),
  ]);

  dispatch({
    type: STAKE_BALANCE_SRFUEL,
    payload: { stakeBalance: calculateStakeBalance(stakeBalance, price) },
  });
  dispatch(getTotalStakedBalance());
};

export const getUnstakes = () => async (dispatch, getState) => {
  const { address } = getState().account;

  const { amount, applicableAt } = await sRfuelStakingContractService.getUnstake(address);
  dispatch({ type: UNSTAKE_TIME_SRFUEL, payload: { unstakedTime: applicableAt } });
  dispatch({ type: UNSTAKED_BALANCE_SRFUEL, payload: { unstakedBalance: amount } });
};

export const getClaimingFeePercent = () => async (dispatch, getState) => {
  const data = await sRfuelStakingContractService.getClaimingFeePercent();
  dispatch({ type: CLAIMING_FEE_PERCENT_SRFUEL, payload: data });
};

export const getEstimatedAPR = () => async (dispatch) => {
  const apr = await estimatedAPR();
  dispatch({ type: APR_SRFUEL, payload: { apr: Number(apr).toFixed(2) } });
};

export const getEstimatedDailyRewards = () => async (dispatch, getState) => {
  const { address } = getState().account;

  const estimatedDailyRewards = await dailyReward(address);
  dispatch({
    type: ESTIMATED_DAILY_REWARDS_SRFUEL,
    payload: { estimatedDailyRewards: `${estimatedDailyRewards}` },
  });
};

export const getEarnedRewards = () => async (dispatch, getState) => {
  const { address } = getState().account;
  const LogType = {
    SENDED: 'SENDED',
    RECEIVED: 'RECEIVED',
  };

  const currentBlockNumber = await web3Service.web3.eth.getBlockNumber();
  const receivingLogs = await web3Service.web3.eth.getPastLogs({
    topics: [Event.TRANSFER, null, `0x${address.slice(2).padStart(64, '0')}`],
    address: config.REACT_APP_SRFUEL_STAKING_ADDRESS,
    fromBlock: 0,
    toBlock: currentBlockNumber,
  });
  const sendingLogs = await web3Service.web3.eth.getPastLogs({
    topics: [Event.TRANSFER, `0x${address.slice(2).padStart(64, '0')}`],
    address: config.REACT_APP_SRFUEL_STAKING_ADDRESS,
    fromBlock: 0,
    toBlock: currentBlockNumber,
  });

  let minBlockNumber = receivingLogs.reduce(
    (acc, log) => Math.min(acc, log.blockNumber),
    Number.MAX_SAFE_INTEGER,
  );
  minBlockNumber = sendingLogs.reduce((acc, log) => Math.min(acc, log.blockNumber), minBlockNumber);

  const blockPrices = new Map();
  const BATCH_SIZE = 10000;

  for (let to = currentBlockNumber + 1; to >= minBlockNumber; to -= BATCH_SIZE) {
    // eslint-disable-next-line no-await-in-loop
    const priceUpdaringLogs = await web3Service.web3.eth.getPastLogs({
      topics: [Event.PRICE_UPDATED],
      address: config.REACT_APP_SRFUEL_STAKING_ADDRESS,
      fromBlock: to - BATCH_SIZE,
      toBlock: to - 1,
    });
    priceUpdaringLogs.forEach((log) => {
      blockPrices.set(log.blockNumber, new BN(log.data.slice(2, 66), 16));
    });
  }

  const logs = [...receivingLogs, ...sendingLogs]
    .map(({ topics, blockNumber, data, transactionIndex }) => {
      const type =
        `0x${topics[1].slice(2 + 64 - 40)}` === address ? LogType.SENDED : LogType.RECEIVED;
      return {
        type,
        block: blockNumber,
        value: new BN(data.slice(2), 16),
        index: transactionIndex,
      };
    })
    .sort((a, b) => {
      if (a.block < b.block) return -1;
      if (a.block > b.block) return 1;
      return a.index - b.index;
    });

  let balance = new BN(0);
  let staked = new BN(0);
  let reward = new BN(0);

  logs.forEach((log) => {
    const { type, value, block } = log;
    const price = blockPrices.get(block);
    if (!price) throw new Error(`Unable to get price of block #${block}`);
    if (type === LogType.RECEIVED) {
      staked = staked.plus(value.times(price).div(1e18));
      balance = balance.plus(value);
    } else {
      reward = reward.plus(balance.times(price).div(1e18).minus(staked.plus(reward)));
      const sended = value.times(price).div(1e18);
      if (reward.lt(sended)) {
        staked = staked.minus(sended.minus(reward));
        reward = new BN(0);
      } else reward = reward.minus(sended);
      balance = balance.minus(value);
    }
  });
  const currentPrice = await getPrice(sRfuelStakingContractService);

  const notLockedReward = balance.times(currentPrice).minus(staked.plus(reward));
  const totalReward = reward.plus(notLockedReward);
  dispatch({
    type: EARNED_REWARDS_BALANCE_SRFUEL,
    payload: { earnedRewards: totalReward.toFixed(0) },
  });
};

export const initData = () => async (dispatch) =>
  Promise.all([
    dispatch(getUnstakes()),
    dispatch(getStakedBalance()),
    dispatch(getEstimatedAPR()),
    dispatch(getEstimatedDailyRewards()),
    dispatch(getClaimingFeePercent()),
    dispatch(getEarnedRewards()),
  ]);
